// C16/C17 pdf-lib stamping pivot (see PdfDocument.ts / TargetPdfDocument.ts
// for the full history): the generated Commitment Letter and target
// statement are the REAL source PDFs (specs/001-raben-country-masteradmin/
// reference/docs, served from public/assets/raben) with the fillable blanks
// stamped in, rather than a react-pdf lookalike -- that is what makes the
// layout and every Polish glyph exactly match the document a carrier
// actually signs.
//
// This module answers one question: "where on the page does a given blank
// live?" -- WITHOUT hardcoding a single x/y. Every position is read off the
// page's own text layer (via pdfjs-dist's getTextContent) relative to a
// label or phrase that already exists next to that blank. If Raben revises
// the source document and a label shifts, the stamped values shift with it
// automatically; if a label disappears entirely, resolution throws instead
// of silently drawing a value in the wrong place -- this is a document a
// carrier signs, so a loud failure beats a wrong one.
//
// Deliberately pdf-lib-free: this file only reads text positions. Drawing
// (embedding a font, calling page.drawText) lives in pdfStamp.ts, which is
// what makes the anchoring logic here testable with plain fixtures.

// eslint-disable-next-line import/no-extraneous-dependencies
import { getDocument } from 'pdfjs-dist/legacy/build/pdf';

// pdfjs-dist's own TextItem/TextMarkedContent types aren't part of its
// public export surface (only reachable via an internal `types/src/...`
// subpath), so this only asks pdfjs' text items for the handful of fields
// it actually needs, checked with a runtime guard below rather than an
// imported type name. That also keeps this module honest about being
// pdf-lib-free and easy to feed with plain fixtures in tests.
export interface TextItemLike {
  str: string;
  transform: number[];
  width: number;
}

export interface TextSegment {
  str: string;
  x: number;
  width: number;
}

export interface TextLine {
  y: number;
  segments: TextSegment[];
  text: string;
}

export type AnchorMode = 'after' | 'before' | 'afterBlank';

export interface AnchorSpec {
  // Exact phrase to locate in the page's text (a single visual line). Must
  // match exactly one place on the page -- findAnchor throws on 0 or 2+.
  phrase: string;
  // 'after': the value starts right where the phrase ends (the common
  // case: a label like "Nazwa firmy:" immediately followed by its blank).
  // 'before': the value ends right where the phrase starts (for a blank
  // that has nothing usable preceding it on the same line, e.g. the
  // target statement's reduction-percentage blank opens a wrapped line --
  // anchor on the "%" that follows it instead).
  // 'afterBlank': the value starts where the next dot leader begins, skipping
  // any padding in between (RG 28.08 12.1). The official German statement
  // pads "Emissionsniveau im Basisjahr:" with ~100pt of trailing spaces
  // before its dots, so 'after' would stamp the value well left of the
  // blank. Fails closed when the first thing after the phrase is not a
  // leader (the document no longer looks like the one this was written for).
  mode: AnchorMode;
}

export interface ResolvedAnchor {
  x: number;
  y: number;
}

const LINE_Y_TOLERANCE = 0.5;

// pdfjs' TextContent.items is `Array<TextItem | TextMarkedContent>` --
// TextMarkedContent entries (only present when includeMarkedContent is
// requested, which we never do) have no `str`/`transform`/`width`, so this
// guard is what lets buildLines accept the raw array without importing
// either type by name.
function isTextItemLike(item: unknown): item is TextItemLike {
  return (
    typeof item === 'object' &&
    item !== null &&
    typeof (item as { str?: unknown }).str === 'string'
  );
}

// Groups a page's text-content items into visual lines (shared baseline)
// and concatenates each line's text in reading order. Source PDFs don't
// always emit one label as one item -- e.g. "Date:" shows up as three
// separate glyph runs ("Dat" / "e" / ":") because of kerning pairs in the
// original document -- so phrase-matching happens against the joined line,
// never a single item's string in isolation.
export function buildLines(items: ReadonlyArray<unknown>): TextLine[] {
  const lines: TextLine[] = [];
  for (const item of items) {
    if (!isTextItemLike(item) || item.str === '') continue;
    const y = item.transform[5];
    let line = lines.find((l) => Math.abs(l.y - y) < LINE_Y_TOLERANCE);
    if (!line) {
      line = { y, segments: [], text: '' };
      lines.push(line);
    }
    line.segments.push({
      str: item.str,
      x: item.transform[4],
      width: item.width,
    });
  }
  for (const line of lines) {
    line.segments.sort((a, b) => a.x - b.x);
    line.text = line.segments.map((s) => s.str).join('');
  }
  return lines;
}

// x position at a given character offset into a line. When the offset
// falls on a segment boundary (the overwhelmingly common case here -- a
// label is its own text-showing operation, ending exactly where the blank
// begins) this uses the NEXT segment's own recorded x rather than this
// segment's computed (x + width) end: the two are usually equal, but real
// PDFs sometimes leave a hairline gap between adjacent glyph runs, and the
// next segment's recorded x is ground truth while (x + width) is only an
// estimate. When a label is fused with its own dot leader into a single
// text-showing operation in the source PDF (observed once, in the target
// statement's base-year-emissions line), this falls back to an
// even-character-width estimate within that one run -- imprecise for a
// proportional font, but it only has to land inside the blank/dot-leader
// area, not on a specific glyph.
function xAtCharIndex(line: TextLine, charIndex: number): number {
  let consumed = 0;
  for (let i = 0; i < line.segments.length; i += 1) {
    const seg = line.segments[i];
    const segLen = seg.str.length;
    const segEnd = consumed + segLen;
    if (charIndex < segEnd) {
      const within = charIndex - consumed;
      const frac = segLen === 0 ? 0 : within / segLen;
      return seg.x + seg.width * frac;
    }
    if (charIndex === segEnd) {
      const next = line.segments[i + 1];
      return next ? next.x : seg.x + seg.width;
    }
    consumed = segEnd;
  }
  const last = line.segments[line.segments.length - 1];
  if (!last) {
    throw new Error('pdfAnchors: cannot resolve a position on an empty line');
  }
  return last.x + last.width;
}

// Dot leaders in the source documents: ASCII dots, and "…" (U+2026), which
// the German and the emission lines of the other statements mix in.
const LEADER_CHAR = /[.…]/;

// x position where the dot leader following char offset `charIndex` begins:
// whitespace after the offset is skipped, and null means the next visible
// character is not a leader (or the line ends). When the leader shares a
// text-showing operation with the label (see xAtCharIndex) the position is
// the same even-character-width estimate.
function leaderStartAfter(line: TextLine, charIndex: number): number | null {
  let consumed = 0;
  for (const seg of line.segments) {
    const segStart = consumed;
    consumed += seg.str.length;
    if (consumed <= charIndex) continue;
    const rest = seg.str.slice(Math.max(charIndex - segStart, 0));
    const lead = rest.search(/\S/);
    if (lead === -1) continue; // padding only, keep looking
    if (!LEADER_CHAR.test(rest[lead])) return null;
    const offset = seg.str.length - rest.length + lead;
    return seg.x + seg.width * (offset / seg.str.length);
  }
  return null;
}

// Finds the one place on the page where `spec.phrase` occurs and returns
// the x/y a value should be drawn at. Throws (rather than guessing) when
// the phrase is missing or shows up more than once -- both mean the source
// document no longer matches what this anchor was written against.
export function findAnchor(
  lines: ReadonlyArray<TextLine>,
  spec: AnchorSpec
): ResolvedAnchor {
  const matches: Array<{ line: TextLine; idx: number }> = [];
  for (const line of lines) {
    let searchFrom = 0;
    for (;;) {
      const idx = line.text.indexOf(spec.phrase, searchFrom);
      if (idx === -1) break;
      matches.push({ line, idx });
      searchFrom = idx + 1;
    }
  }
  if (matches.length === 0) {
    throw new Error(
      `Supplier Commitment PDF: anchor phrase not found: ${JSON.stringify(
        spec.phrase
      )}. ` +
        'The source document text no longer matches this anchor -- it needs a human look, not a guessed position.'
    );
  }
  if (matches.length > 1) {
    const hits = matches.map(
      ({ line }) => `y=${line.y} in line: ${JSON.stringify(line.text)}`
    );
    const phrase = JSON.stringify(spec.phrase);
    const list = hits.join('\n');
    throw new Error(
      `Supplier Commitment PDF: anchor phrase ${phrase} is ambiguous (${matches.length} matches):\n${list}`
    );
  }
  const { line, idx } = matches[0];
  const phraseEnd = idx + spec.phrase.length;
  if (spec.mode === 'afterBlank') {
    const x = leaderStartAfter(line, phraseEnd);
    if (x === null) {
      throw new Error(
        `Supplier Commitment PDF: no dot leader follows anchor phrase ${JSON.stringify(
          spec.phrase
        )} ` +
          `in line: ${JSON.stringify(
            line.text
          )}. The source document no longer matches this anchor.`
      );
    }
    return { x, y: line.y };
  }
  return {
    x: xAtCharIndex(line, spec.mode === 'after' ? phraseEnd : idx),
    y: line.y,
  };
}

// Parses `pdfBytes` with pdfjs-dist and returns the text lines for one
// page (1-indexed, matching pdfjs' own getPage convention).
export async function getPageLines(
  pdfBytes: Uint8Array,
  pageNumber: number
): Promise<TextLine[]> {
  const doc = await getDocument({ data: pdfBytes }).promise;
  try {
    const page = await doc.getPage(pageNumber);
    const content = await page.getTextContent();
    return buildLines(content.items);
  } finally {
    await doc.destroy();
  }
}
